import React, {
  createContext,
  useContext,
  useEffect,
  ReactNode,
  useState,
} from 'react';
import { SSEEvent, SSEEventType } from '../services/sse';
import sseService from '../services/sse';

interface SSEContextType {
  isConnected: boolean;
  on: (
    eventType: SSEEventType,
    handler: (event: SSEEvent) => void,
  ) => () => void;
  onAccountUpdate: (handler: (data: any) => void) => () => void;
  onPositionChange: (handler: (data: any) => void) => () => void;
  onBalanceChange: (handler: (data: any) => void) => () => void;
  onSyncComplete: (handler: (data: any) => void) => () => void;
  onError: (handler: (data: any) => void) => () => void;
}

const SSEContext = createContext<SSEContextType | undefined>(undefined);

export const useSSE = () => {
  const context = useContext(SSEContext);
  if (context === undefined) {
    throw new Error('useSSE must be used within an SSEProvider');
  }
  return context;
};

interface SSEProviderProps {
  children: ReactNode;
  authToken?: string | null;
}

export const SSEProvider: React.FC<SSEProviderProps> = ({
  children,
  authToken,
}) => {
  const [isConnected, setIsConnected] = useState(
    sseService.getConnectionStatus(),
  );

  useEffect(() => {
    const unsubscribe = sseService.onConnectionStatusChange(setIsConnected);
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (authToken) {
      sseService.setAuthToken(authToken);
      sseService.connect();
    } else {
      sseService.disconnect();
    }

    return () => {
      sseService.disconnect();
    };
  }, [authToken]);

  const value: SSEContextType = {
    isConnected,
    on: sseService.on.bind(sseService),
    onAccountUpdate: sseService.onAccountUpdate.bind(sseService),
    onPositionChange: sseService.onPositionChange.bind(sseService),
    onBalanceChange: sseService.onBalanceChange.bind(sseService),
    onSyncComplete: sseService.onSyncComplete.bind(sseService),
    onError: sseService.onError.bind(sseService),
  };

  return <SSEContext.Provider value={value}>{children}</SSEContext.Provider>;
};
